import { Step } from '../../types/tutor';
import { Button } from '../../components/ui/button';
import { Play, Pause, SkipForward, Loader2, ChevronLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useState, useRef, useEffect } from 'react';
import { getTTS } from '../../api/tutor';
import { API_HOST } from '@/lib/config';
import { LatexText } from '../../components/ui/LatexText';

interface CoachPanelProps {
  currentStep: Step;
  nextStepText?: string;
  nextStepAudioUrl?: string;
  onNextStep: () => void;
  onPrevStep?: () => void;
  className?: string;
}

export function CoachPanel({ currentStep, nextStepText, nextStepAudioUrl, onNextStep, onPrevStep, className }: CoachPanelProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentStepIdRef = useRef(currentStep.step_id);
  const isMountedRef = useRef(true);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update ref when step changes
  useEffect(() => {
    currentStepIdRef.current = currentStep.step_id;
  }, [currentStep.step_id]);

  // Reset state when step changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (isMountedRef.current) {
      if (currentStep.audio_url) {
        const fullUrl = currentStep.audio_url.startsWith('http') ? currentStep.audio_url : `${API_HOST}${currentStep.audio_url}`;
        setAudioUrl(fullUrl);
      } else {
        setAudioUrl(null);
      }
      setIsPlaying(false);
      setIsLoadingAudio(false);
    }
  }, [currentStep.step_id, currentStep.audio_url]);

  // Auto-fetch audio when step changes
  useEffect(() => {
    const fetchAudio = async () => {
      if (currentStep.audio_url) {
        const fullUrl = currentStep.audio_url.startsWith('http') ? currentStep.audio_url : `${API_HOST}${currentStep.audio_url}`;
        setAudioUrl(fullUrl);
        return;
      }
      if (audioUrl || isLoadingAudio) return;

      const requestedStepId = currentStep.step_id;
      if (isMountedRef.current) setIsLoadingAudio(true);
      
      try {
        const res = await getTTS({
          text: currentStep.speak_text,
          step_id: currentStep.step_id
        });
        
        // Check if step changed during fetch and component is still mounted
        if (isMountedRef.current && currentStepIdRef.current === requestedStepId) {
          const fullUrl = `${API_HOST}${res.audio_url}`;
          setAudioUrl(fullUrl);
        }
      } catch (error) {
        console.error("Auto-fetch audio failed:", error);
      } finally {
        if (isMountedRef.current && currentStepIdRef.current === requestedStepId) {
          setIsLoadingAudio(false);
        }
      }
    };

    fetchAudio();
  }, [currentStep.step_id, currentStep.speak_text, currentStep.audio_url]);

  // Prefetch next step audio when current audio starts playing
  useEffect(() => {
    const prefetchNext = async () => {
      if (isPlaying && nextStepText && !nextStepAudioUrl) {
        try {
          await getTTS({
            text: nextStepText,
            step_id: 'prefetch-next'
          });
          console.log("Prefetched next step audio");
        } catch (e) {
          // Ignore prefetch errors
          console.debug("Prefetch failed", e);
        }
      }
    };

    prefetchNext();
  }, [isPlaying, nextStepText]);

  // Auto-play when audioUrl is ready
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (isMountedRef.current) setIsPlaying(true);
          })
          .catch(err => {
            if (err.name !== 'AbortError') {
              console.warn("Auto-play blocked by browser policy:", err);
            }
            // We don't set isPlaying to true here, so UI shows Play button
          });
      }
    }
  }, [audioUrl]);

  const togglePlay = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    // Resume if we have URL
    if (audioUrl) {
      try {
        await audioRef.current?.play();
        if (isMountedRef.current) setIsPlaying(true);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error("Resume failed:", err);
        }
      }
      return;
    }

    // Fetch TTS
    const requestedStepId = currentStep.step_id;
    try {
      if (isMountedRef.current) setIsLoadingAudio(true);
      const res = await getTTS({
        text: currentStep.speak_text,
        step_id: currentStep.step_id
      });
      
      if (currentStepIdRef.current !== requestedStepId || !isMountedRef.current) {
        // Step changed during fetch, ignore result
        return;
      }

      // Construct full URL
      const fullUrl = `${API_HOST}${res.audio_url}`;
      setAudioUrl(fullUrl);
    } catch (error) {
      console.error("TTS fetch failed:", error);
    } finally {
      if (isMountedRef.current && currentStepIdRef.current === requestedStepId) {
        setIsLoadingAudio(false);
      }
    }
  };

  return (
    <div className={cn("flex flex-col h-full bg-slate-50 border-l border-slate-200", className)}>
      
      {/* Audio Element */}
      <audio 
        ref={audioRef}
        src={audioUrl || undefined}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />

      {/* Content Area - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* 3. Highlights Summary */}
        {currentStep.highlights.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">本步重点</h3>
              <div className="space-y-2">
                {currentStep.highlights.map((h, i) => (
                  <div 
                    key={i}
                    className={cn(
                      "p-3 rounded-lg border text-sm flex gap-3 items-start shadow-sm bg-white",
                      h.type === 'key' && "border-amber-200 bg-amber-50/30",
                      h.type === 'pitfall' && "border-rose-200 bg-rose-50/30",
                      h.type === 'result' && "border-emerald-200 bg-emerald-50/30"
                    )}
                  >
                    <div className={cn(
                      "w-1.5 h-1.5 rounded-full mt-1.5 shrink-0",
                      h.type === 'key' && "bg-amber-500",
                      h.type === 'pitfall' && "bg-rose-500",
                      h.type === 'result' && "bg-emerald-500"
                    )} />
                    <LatexText inline className="text-slate-700">{h.text}</LatexText>
                  </div>
                ))}
              </div>
            </div>
          )
        }
      </div>

      {/* Footer Control Bar */}
      <div className="p-4 border-t border-slate-200 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10">
        <div className="flex items-center gap-3">
          {/* Prev Button */}
          {onPrevStep && (
            <Button variant="outline" size="icon" onClick={onPrevStep} className="shrink-0 rounded-full w-10 h-10 border-slate-200 hover:bg-slate-100 text-slate-600">
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}

          {/* Audio Control (Compact) */}
          <div className="flex-1 flex items-center gap-3 bg-slate-50 rounded-full p-1.5 pr-4 border border-slate-200 shadow-sm overflow-hidden min-w-0">
            <Button 
              size="icon" 
              variant={isPlaying ? "default" : "secondary"} 
              className={cn("rounded-full w-9 h-9 shrink-0 transition-all", isPlaying ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md animate-pulse" : "bg-white hover:bg-slate-100 text-slate-700 border border-slate-200")}
              onClick={togglePlay}
              disabled={isLoadingAudio}
            >
              {isLoadingAudio ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4 ml-0.5" />
              )}
            </Button>
            
            <div className="flex flex-col min-w-0 flex-1 justify-center">
              <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-slate-700 truncate leading-tight">AI 老师讲解</span>
                  {/* Waveform (Mini) */}
                  <div className="flex items-end gap-0.5 h-3">
                    {[40, 70, 50, 90].map((h, i) => (
                      <div key={i} className={cn(
                        "w-0.5 bg-blue-400 rounded-full",
                        isPlaying && "animate-pulse"
                      )} style={{ height: `${h}%`, animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
              </div>
            </div>
          </div>

          {/* Next Button */}
          <Button 
            onClick={onNextStep}
            className="shrink-0 rounded-full h-10 px-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow-md hover:shadow-lg transition-all"
          >
            <span className="mr-1">下一步</span>
            <SkipForward className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
